import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable, EmptyState, FilterBar, FilterChips, Pagination, Pill, SelectField, fmtBRL, type Column } from "@atlas/ui/web";
import type { EstadoRefinanciamento, Refinanciamento } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { useFiltros } from "../../lib/use-filtros";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { ESTADO_REFIN_LABEL_OPERADOR, fmtDataHora, fmtTaxaFracao, pillDoEstadoRefin } from "../../lib/refinanciamento";

const ESTADOS: EstadoRefinanciamento[] = ["solicitado", "ofertado", "aceito", "efetivado", "recusado", "cancelado", "expirado"];

type Linha = Refinanciamento & { adfs: string };

/** Acompanhamento da averbadora: todo refinanciamento, de todo banco. */
export function AverbadoraRefinanciamentos() {
  const q = useQuery({
    queryKey: ["admin", "refinanciamentos"],
    queryFn: () => atlas.refinanciamento.todos(),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
  const [estado, setEstado] = useState<"" | EstadoRefinanciamento>("");
  const [banco, setBanco] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  const base: Linha[] = useMemo(
    () => (q.data?.itens ?? []).map((r) => ({ ...r, adfs: [...r.origens.map((o) => o.adf), r.adfSucessor ?? ""].join(" ") })),
    [q.data],
  );
  const termo = busca.trim();
  const filtros = useMemo(() => ({
    estado: estado ? (r: Linha) => r.estado === estado : null,
    banco: banco ? (r: Linha) => r.bancoNome === banco : null,
    busca: termo ? (r: Linha) => matchAnyKeys(r, termo, ["id", "servidorNome", "matricula", "cpfMasked", "adfs", "convenioNome"]) : null,
  }), [estado, banco, termo]);
  const linhas = useMemo(() => aplicarFiltros(base, filtros), [base, filtros]);
  const semEstado = useMemo(() => aplicarFiltros(base, filtros, "estado"), [base, filtros]);
  const contEstado = useMemo(() => contagens(semEstado, (r) => r.estado), [semEstado]);
  const bancos = useMemo(() => [...new Set(base.map((r) => r.bancoNome))].sort(), [base]);

  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "estado", ligado: estado !== "", limpar: () => setEstado("") },
      { nome: "banco", ligado: banco !== "", limpar: () => setBanco("") },
    ],
    setPagina,
  );
  const { filtrar } = filtrosUi;
  const daPagina = recortar(linhas, pagina, porPagina);

  const columns: Column<Linha>[] = [
    { key: "estado", header: "Estado", render: (r) => <Pill variant={pillDoEstadoRefin(r.estado)}>{ESTADO_REFIN_LABEL_OPERADOR[r.estado]}</Pill> },
    { key: "id", header: "Refinanciamento", mono: true, render: (r) => r.id },
    { key: "bancoNome", header: "Consignatária" },
    { key: "convenioNome", header: "Convênio" },
    {
      key: "servidor",
      header: "Servidor",
      render: (r) => (
        <>
          <div>{r.servidorNome}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.cpfMasked} / {r.matricula}</div>
        </>
      ),
    },
    { key: "origens", header: "Origens", mono: true, render: (r) => r.origens.map((o) => `${o.adf}${o.situacao ? ` (${o.situacao})` : ""}`).join(", ") },
    {
      key: "proposta",
      header: "Condições",
      render: (r) =>
        r.proposta
          ? `${r.proposta.prazo}x de ${fmtBRL(r.proposta.valorParcela)} · ${fmtTaxaFracao(r.proposta.taxaAm)} · troco ${fmtBRL(r.proposta.troco)}`
          : "—",
    },
    { key: "sucessor", header: "Sucessor", mono: true, render: (r) => (r.sucessor ? `${r.sucessor.adf} (${r.sucessor.situacao})` : "—") },
    { key: "canal", header: "Canal", render: (r) => `${r.iniciadoPor === "banco" ? "Consignatária" : "Servidor"} · ${r.canal}` },
    { key: "criadoEm", header: "Criado em", render: (r) => fmtDataHora(r.criadoEm) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span className="eyebrow">Operação</span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Refinanciamentos</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 760 }}>
          Renegociações com a própria consignatária. O contrato sucessor entra como REFIN e só substitui as origens na aplicação da
          ADF; as regras (parcelas pagas mínimas, troco, unificação, validade) ficam na configuração de cada convênio.
        </p>
      </header>

      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por refinanciamento, servidor, CPF, matrícula, ADF ou convênio"
        {...filtrosUi.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Estado"
            value={estado}
            onChange={(v) => filtrar(setEstado)(v as "" | EstadoRefinanciamento)}
            options={[
              { value: "", label: "Todos", count: semEstado.length },
              ...ESTADOS.map((e) => ({ value: e, label: ESTADO_REFIN_LABEL_OPERADOR[e], count: contEstado[e] ?? 0 })),
            ]}
          />
        }
      >
        <SelectField
          label="Consignatária"
          value={banco}
          onChange={(e) => filtrar(setBanco)(e.target.value)}
          options={[{ value: "", label: "Todos" }, ...bancos.map((b) => ({ value: b, label: b }))]}
        />
      </FilterBar>

      {q.isError ? (
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13 }}>
          {mensagemDeErro(q.error, "Não foi possível carregar os refinanciamentos.")}
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.id}
        emptyState={
          <EmptyState
            motivo={motivoVazio(base.length, linhas.length)}
            filtroVazio="Nenhum refinanciamento com esses filtros."
            semDados="Nenhum refinanciamento registrado ainda."
            onLimpar={filtrosUi.limparFiltros}
          />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="refinanciamentos"
      />
    </div>
  );
}
